import { apiRequest, canUseBackend } from './apiClient';
import { getCurrentUser, getUsers } from '../utils/authStorage';

let users = [];
let friends = {};
let books = {};

let friendRequests = [];

const syncUsersFromAuthStorage = () => {
    const localUsers = Array.isArray(getUsers()) ? getUsers() : [];
    const currentUser = getCurrentUser();
    const mergedUsers = [...localUsers];
    if (currentUser?.id && !mergedUsers.some((entry) => String(entry.id) === String(currentUser.id))) {
        mergedUsers.push(currentUser);
    }

    users = mergedUsers.map((entry) => ({
        id: entry.id,
        name: entry.name || 'Reader',
        username: String(entry.username || '').trim().toLowerCase(),
        email: entry.email || ''
    }));

    return users;
};

const getPreferredUserId = (providedUserId) => {
    const currentUser = getCurrentUser();
    if (currentUser?.id) return currentUser.id;
    if (providedUserId !== null && providedUserId !== undefined && String(providedUserId).trim()) {
        return providedUserId;
    }
    return '';
};

const sleep = (duration) => new Promise((resolve) => setTimeout(resolve, duration));
const normalizeUsername = (value) => String(value || '').trim().toLowerCase();
const normalizeUsernameInput = (value) => normalizeUsername(String(value || '').replace(/^@+/, ''));
const isValidUsername = (value) => /^[a-z0-9]{3,24}$/.test(normalizeUsernameInput(value));

const toPublicUser = (user) => (user ? {
    id: user.id,
    name: user.name,
    username: user.username || '',
    email: user.email || ''
} : null);

const buildLocalRequestView = (request) => ({
    id: request.id,
    status: request.status,
    createdAt: request.createdAt,
    respondedAt: request.respondedAt || null,
    fromUser: toPublicUser(users.find((entry) => String(entry.id) === String(request.fromUserId))),
    toUser: toPublicUser(users.find((entry) => String(entry.id) === String(request.toUserId)))
});

export const getFriends = async (userId) => {
    if (canUseBackend()) {
        try {
            const response = await apiRequest({
                method: 'GET',
                path: '/api/friends'
            });
            return Array.isArray(response.data?.friends) ? response.data.friends : [];
        } catch {
            // Fallback to local in-memory data if backend call fails.
        }
    }

    await sleep(250);
    syncUsersFromAuthStorage();
    const resolvedUserId = getPreferredUserId(userId);
    if (!resolvedUserId) return [];
    const friendIds = friends[resolvedUserId] || [];
    const friendDetails = friendIds.map(id => users.find(u => String(u.id) === String(id))).filter(Boolean);
    return friendDetails;
};

export const getFriendBooks = async (friendId) => {
    if (canUseBackend()) {
        try {
            const response = await apiRequest({
                method: 'GET',
                path: `/api/friends/${encodeURIComponent(friendId)}/books`
            });
            return Array.isArray(response.data?.books) ? response.data.books : [];
        } catch {
            // Fallback to local in-memory data if backend call fails.
        }
    }

    await sleep(250);
    return books[friendId] || [];
};

export const addFriend = async (userId, rawFriendUsername) => {
    const normalizedUsername = normalizeUsernameInput(rawFriendUsername);
    if (!isValidUsername(normalizedUsername)) {
        throw new Error('Enter a valid username (3-24 letters or numbers).');
    }

    if (canUseBackend()) {
        try {
            const response = await apiRequest({
                method: 'POST',
                path: '/api/friends',
                data: { username: normalizedUsername }
            });

            return {
                friend: response.data?.friend,
                created: false,
                alreadyFriend: Boolean(response.data?.alreadyFriend),
                requestPending: Boolean(response.data?.requestPending),
                accepted: Boolean(response.data?.accepted),
                direction: response.data?.direction || '',
                requestId: response.data?.requestId || ''
            };
        } catch (error) {
            const backendMessage = error?.response?.data?.error;
            throw new Error(backendMessage || 'Could not add this friend right now.');
        }
    }

    await sleep(240);
    syncUsersFromAuthStorage();
    const resolvedUserId = getPreferredUserId(userId);
    if (!resolvedUserId) {
        throw new Error('Sign in to add friends.');
    }

    const friend = users.find((entry) => normalizeUsername(entry.username) === normalizedUsername);
    if (!friend) {
        throw new Error('No account found with that username.');
    }
    if (String(friend.id) === String(resolvedUserId)) {
        throw new Error('You cannot add yourself.');
    }
    const currentFriendIds = Array.isArray(friends[resolvedUserId]) ? [...friends[resolvedUserId]] : [];
    const alreadyFriend = currentFriendIds.includes(friend.id);

    if (!alreadyFriend) {
        currentFriendIds.push(friend.id);
    }
    friends = {
        ...friends,
        [resolvedUserId]: currentFriendIds
    };

    const reciprocalFriendIds = Array.isArray(friends[friend.id]) ? [...friends[friend.id]] : [];
    if (!reciprocalFriendIds.includes(resolvedUserId)) {
        reciprocalFriendIds.push(resolvedUserId);
    }
    friends = {
        ...friends,
        [friend.id]: reciprocalFriendIds
    };

    if (!books[friend.id]) {
        books = { ...books, [friend.id]: [] };
    }

    return {
        friend,
        created: false,
        alreadyFriend,
        requestPending: false,
        accepted: !alreadyFriend,
        direction: '',
        requestId: ''
    };
};

export const getFriendRequests = async (userId) => {
    if (canUseBackend()) {
        try {
            const response = await apiRequest({
                method: 'GET',
                path: '/api/friends/requests'
            });

            return {
                incoming: Array.isArray(response.data?.incoming) ? response.data.incoming : [],
                outgoing: Array.isArray(response.data?.outgoing) ? response.data.outgoing : []
            };
        } catch {
            // Fallback to local in-memory data if backend call fails.
        }
    }

    await sleep(180);
    syncUsersFromAuthStorage();
    const resolvedUserId = getPreferredUserId(userId);
    if (!resolvedUserId) return { incoming: [], outgoing: [] };
    const incoming = friendRequests
        .filter((request) => String(request.toUserId) === String(resolvedUserId) && request.status === 'pending')
        .map((request) => buildLocalRequestView(request));

    const outgoing = friendRequests
        .filter((request) => String(request.fromUserId) === String(resolvedUserId) && request.status === 'pending')
        .map((request) => buildLocalRequestView(request));

    return { incoming, outgoing };
};

export const respondToFriendRequest = async (requestId, action) => {
    const normalizedAction = String(action || '').trim().toLowerCase();
    if (normalizedAction !== 'accept' && normalizedAction !== 'decline') {
        throw new Error('Invalid friend request action.');
    }

    if (canUseBackend()) {
        try {
            const response = await apiRequest({
                method: 'POST',
                path: `/api/friends/requests/${encodeURIComponent(requestId)}/${normalizedAction}`
            });
            return {
                request: response.data?.request || null,
                friend: response.data?.friend || null
            };
        } catch (error) {
            const backendMessage = error?.response?.data?.error;
            throw new Error(backendMessage || 'Could not update this friend request right now.');
        }
    }

    await sleep(180);
    syncUsersFromAuthStorage();
    const resolvedUserId = getPreferredUserId();
    if (!resolvedUserId) {
        throw new Error('Sign in to respond to friend requests.');
    }
    const requestIndex = friendRequests.findIndex(
        (request) =>
            String(request.id) === String(requestId) &&
            String(request.toUserId) === String(resolvedUserId) &&
            request.status === 'pending'
    );

    if (requestIndex < 0) {
        throw new Error('Friend request not found.');
    }

    const target = { ...friendRequests[requestIndex] };
    target.status = normalizedAction === 'accept' ? 'accepted' : 'declined';
    target.respondedAt = new Date().toISOString();
    friendRequests = friendRequests.map((request, index) => (index === requestIndex ? target : request));

    if (normalizedAction === 'accept') {
        const myFriendIds = Array.isArray(friends[resolvedUserId]) ? [...friends[resolvedUserId]] : [];
        if (!myFriendIds.includes(target.fromUserId)) myFriendIds.push(target.fromUserId);
        friends = { ...friends, [resolvedUserId]: myFriendIds };

        const reciprocal = Array.isArray(friends[target.fromUserId]) ? [...friends[target.fromUserId]] : [];
        if (!reciprocal.includes(resolvedUserId)) reciprocal.push(resolvedUserId);
        friends = { ...friends, [target.fromUserId]: reciprocal };
    }

    const friend = users.find((entry) => String(entry.id) === String(target.fromUserId));
    return {
        request: buildLocalRequestView(target),
        friend: toPublicUser(friend)
    };
};

export const removeFriend = async (friendId, userId) => {
    if (canUseBackend()) {
        try {
            const response = await apiRequest({
                method: 'DELETE',
                path: `/api/friends/${encodeURIComponent(friendId)}`
            });
            return {
                success: Boolean(response.data?.success),
                removedFriendId: response.data?.removedFriendId || friendId
            };
        } catch (error) {
            const backendMessage = error?.response?.data?.error;
            throw new Error(backendMessage || 'Could not remove this friend right now.');
        }
    }

    await sleep(180);
    syncUsersFromAuthStorage();
    const resolvedUserId = getPreferredUserId(userId);
    if (!resolvedUserId) {
        throw new Error('Sign in to remove friends.');
    }
    const hasFriend = Array.isArray(friends[resolvedUserId]) &&
        friends[resolvedUserId].some((id) => String(id) === String(friendId));
    if (!hasFriend) {
        throw new Error('Friend not found in your list.');
    }

    const mine = Array.isArray(friends[resolvedUserId]) ? friends[resolvedUserId].filter((id) => String(id) !== String(friendId)) : [];
    const theirs = Array.isArray(friends[friendId]) ? friends[friendId].filter((id) => String(id) !== String(resolvedUserId)) : [];
    friends = { ...friends, [resolvedUserId]: mine, [friendId]: theirs };

    friendRequests = friendRequests.map((request) => {
        const betweenUsers =
            (String(request.fromUserId) === String(resolvedUserId) && String(request.toUserId) === String(friendId)) ||
            (String(request.fromUserId) === String(friendId) && String(request.toUserId) === String(resolvedUserId));
        if (!betweenUsers || request.status !== 'pending') return request;
        return {
            ...request,
            status: 'declined',
            respondedAt: new Date().toISOString()
        };
    });

    return { success: true };
};

export const __resetLocalBackendData = () => {
    friends = {};
    books = {};
    friendRequests = [];
    syncUsersFromAuthStorage();
};
